// Пайплайн производства билетов (W3): researcher → IT → design → IT → печать → QA.
// Роли: users.team_role researcher | it (+manager видит и правит всё).
import { z } from 'zod';

export const TIXPIPE_STAGES = [
  'found', 'har_uploaded', 'events_manifest', 'manifest_checked',
  'template_ready', 'replicated', 'print_pdf_uploaded', 'print_pdf_approved',
  'done', 'rejected',
] as const;
export const TixpipeStageSchema = z.enum(TIXPIPE_STAGES);
export type TixpipeStage = z.infer<typeof TixpipeStageSchema>;

export const TixpipeManifestSchema = z.object({
  id: z.string(),
  kind: z.enum(['events', 'print_pdf']),
  version: z.number().int(),
  file_name: z.string(),
  events_count: z.number().int(),
  uploaded_by: z.string(),
  uploaded_at: z.string(),
  auto_check_status: z.string(),
  auto_check: z.array(z.object({
    rule: z.string(),
    pass: z.boolean(),
    details: z.string(),
  })).default([]),
  qa_status: z.string(),
  qa_comment: z.string(),
  qa_at: z.string(),
});
export type TixpipeManifest = z.infer<typeof TixpipeManifestSchema>;

export const TixpipePlatformSchema = z.object({
  id: z.string(),
  platform: z.string(),
  country: z.string(),
  url: z.string(),
  stage: TixpipeStageSchema,
  ticket_bought: z.boolean(),
  has_har: z.boolean(),
  har_uploaded_at: z.string(),
  notes: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
  manifests: z.array(TixpipeManifestSchema),
});
export type TixpipePlatform = z.infer<typeof TixpipePlatformSchema>;

export const TixpipeListResSchema = z.object({
  role: z.string(),
  platforms: z.array(TixpipePlatformSchema),
});
export type TixpipeListRes = z.infer<typeof TixpipeListResSchema>;

export const TixpipeCreatePlatformReqSchema = z.object({
  platform: z.string().trim().min(2).max(80),
  country: z.string().trim().length(2),
  url: z.string().trim().max(400).default(''),
  notes: z.string().trim().max(2000).default(''),
});

export const TixpipeUploadHarReqSchema = z.object({
  platform_id: z.string().trim().min(1).max(64),
  fileBase64: z.string().min(1).max(40_000_000),
  fileName: z.string().trim().min(1).max(200),
  ticket_bought: z.boolean().default(true),
});

// Манифест архивных мероприятий: JSON-массив событий. Поля события —
// регламентные; авто-чек валидирует структуру и «архивность» дат.
export const TixpipeEventSchema = z.object({
  title: z.string().trim().min(1),
  date: z.string().trim().min(4),          // ISO дата события (в прошлом)
  venue: z.string().trim().default(''),
  city: z.string().trim().default(''),
  url: z.string().trim().default(''),
  price: z.string().trim().default(''),
  category: z.string().trim().default(''),
});
export type TixpipeEvent = z.infer<typeof TixpipeEventSchema>;

export const TixpipeUploadEventsReqSchema = z.object({
  platform_id: z.string().trim().min(1).max(64),
  fileName: z.string().trim().max(200).default('events.json'),
  events: z.array(z.record(z.string(), z.unknown())).min(1).max(5000),
});

export const TixpipeUploadPrintPdfReqSchema = z.object({
  platform_id: z.string().trim().min(1).max(64),
  fileBase64: z.string().min(1).max(60_000_000),
  fileName: z.string().trim().min(1).max(200),
});

export const TixpipeQaReqSchema = z.object({
  manifest_id: z.string().trim().min(1).max(64),
  approved: z.boolean(),
  comment: z.string().trim().max(2000).default(''),
});

export const TixpipeRenamePlatformReqSchema = z.object({
  platform_id: z.string().trim().min(1).max(64),
  platform: z.string().trim().min(2).max(80),
});
export type TixpipeRenamePlatformReq = z.infer<typeof TixpipeRenamePlatformReqSchema>;

export const TixpipeSetStageReqSchema = z.object({
  platform_id: z.string().trim().min(1).max(64),
  stage: TixpipeStageSchema,
});

export const TixpipeOkResSchema = z.object({
  ok: z.boolean(),
  stage: TixpipeStageSchema.optional(),
  manifest: TixpipeManifestSchema.optional(),
});
export type TixpipeOkRes = z.infer<typeof TixpipeOkResSchema>;

export const TixpipeDownloadReqSchema = z.object({
  platform_id: z.string().trim().min(1).max(64),
  what: z.enum(['har', 'manifest', 'print_pdf']),
  manifest_id: z.string().trim().max(64).default(''),
});
